import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { Button } from '@treezor/design-system';

import {
  IAttributeConfig,
  TFilterDirection,
  TFilterInitialFlow,
  TFilterOperation,
  TFilterStatus,
  TOperationsFiltersForm,
} from '../../store';
import { TCommonFilters } from '../../types';

import {
  operationDirections,
  operationStatus,
  TOperationFilterStatus,
  initialFlows,
  operationsZones,
  operationtypes,
  TInitialFlow,
} from '@/store/models/operation.model';

import { FiltersContainer } from '@/ui-kit/table-filters/UiFilters';
import { AmountFilter } from '@/ui-kit/table-filters/AmountFilter';

import { DateRangeFilter } from '@/ui-kit/table-filters/DateRangeFilter';
import { CheckboxesFilter, IFilterOptions } from '@/ui-kit/table-filters/CheckboxesFilter';

import { setAttributesValue, useAttributes, useDefaultAttributesValues } from './operationsHelpers';

const filterByOperationsTypes = Object.keys(operationtypes)
  .filter((key) =>
    [
      operationtypes.CHECK,
      operationtypes.CARD_TOPUP,
      operationtypes.BANK_TRANSFERT,
      operationtypes.INT_BANK_TRANSFERT,
      operationtypes.WALLET_TRANSFERT,
      operationtypes.CARD_TRANSACTION,
      operationtypes.BANK_DIRECT_DEBIT,
      operationtypes.FEES,
      operationtypes.CREDIT_NOTE,
    ].includes(operationtypes[key]),
  )
  .map((key) => operationtypes[key]);

const statusFilter: TOperationFilterStatus[] = [
  operationStatus.AUTHORIZED,
  operationStatus.SETTLED,
  operationStatus.DECLINED,
  operationStatus.CANCELED,
].map((key) => operationStatus[key]);

const filterByInitialFlow: TInitialFlow[] = Object.keys(initialFlows).map((key) => initialFlows[key]);

const directionsFilter = Object.keys(operationDirections).map((key) => operationDirections[key]);

export interface IWalletOperationsFormFiltersProps {
  onChangeFilters: (filters?: IAttributeConfig[]) => void;
}

export const WalletOperationsFormFilters = ({ onChangeFilters }: IWalletOperationsFormFiltersProps) => {
  const { t } = useTranslation();
  const filters = useAttributes({ zone: operationsZones.FILTERS_FORM });
  const defaultValues = useDefaultAttributesValues(filters);

  const methods = useForm<TOperationsFiltersForm>({ defaultValues });

  const handleChangeFilter = useCallback(
    (filtersValues: TCommonFilters) => {
      const attributes = setAttributesValue(filters, filtersValues);
      onChangeFilters(attributes);
    },
    [onChangeFilters, filters],
  );

  const handleClearFilters = () => {
    methods.reset();
    onChangeFilters();
  };

  const operationsTypes: IFilterOptions<TFilterOperation>[] = [
    {
      options: filterByOperationsTypes.map((operation) => ({
        label: t(`monetics.wallet.operation.type.${operation}`),
        name: operation,
        value: operation,
      })),
    },
  ];

  const operationsDirections: IFilterOptions<TFilterDirection>[] = [
    {
      options: directionsFilter.map((direction) => ({
        label: t(`monetics.wallet.operation.direction.${direction}`),
        name: direction,
        value: direction,
      })),
    },
  ];

  const initialFlows: IFilterOptions<TFilterInitialFlow>[] = [
    {
      options: filterByInitialFlow.map((initialFlow) => ({
        label: t(`monetics.wallet.operation.initialFlow.${initialFlow}`),
        name: initialFlow,
        value: initialFlow,
      })),
    },
  ];

  const status: IFilterOptions<TFilterStatus>[] = [
    {
      options: statusFilter.map((status) => ({
        label: t(`components.molecules.statusLabel.${status}`),
        name: status,
        value: status,
      })),
    },
  ];

  return (
    <FiltersContainer>
      <FormProvider {...methods}>
        <DateRangeFilter
          key={'DateRangeFilter'}
          label={t('components.filters.date.title')}
          onFilterChange={handleChangeFilter}
          todayButtonLabel={t('components.atoms.datefield.today')}
          icon={'date_range'}
          id="date-range-filter"
        />
        <AmountFilter onFilterChange={handleChangeFilter} key={'AmountFilter'} />
        <CheckboxesFilter
          key={'statusFilter'}
          onFilterChange={handleChangeFilter}
          filtersOptions={status}
          columns={2}
          name="status"
          popoverOptions={{
            label: t('pages.currentProfiles.wallets.filters.status.title'),
            icon: 'filter_list',
          }}
        />
        <CheckboxesFilter
          key={'initialFlowFilter'}
          onFilterChange={handleChangeFilter}
          filtersOptions={initialFlows}
          columns={2}
          name="initialFlow"
          popoverOptions={{
            label: t('pages.currentProfiles.wallets.filters.initialFlow.title'),
            icon: 'filter_list',
          }}
        />
        <CheckboxesFilter
          key={'typeFilter'}
          onFilterChange={handleChangeFilter}
          filtersOptions={operationsTypes}
          columns={2}
          name="operationType"
          popoverOptions={{
            label: t('pages.currentProfiles.wallets.filters.operation.title'),
            icon: 'filter_list',
          }}
        />
        <CheckboxesFilter
          key={'directionFilter'}
          onFilterChange={handleChangeFilter}
          filtersOptions={operationsDirections}
          name="direction"
          popoverOptions={{
            label: t('pages.currentProfiles.wallets.filters.direction.title'),
          }}
        />
      </FormProvider>
      <Button
        id="operations-filters-clear-btn"
        onClick={handleClearFilters}
        variant="ghost"
        size="s"
        className="pl-5"
        testId="wallets-filters-clear"
      >
        {t('pages.currentProfiles.wallets.filters.clear')}
      </Button>
    </FiltersContainer>
  );
};
